import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {DipRegistrationService} from '../dip-registration.service';
import {
  DipRegistrationPhoneNumberModalComponent
} from '../modals/dip-registration-phone-number-modal/dip-registration-phone-number-modal.component';
import {DipRegistrationErrorsHandler} from '../dip-registration-error/dip-registration-errors.handler';
import {
  DipRegistrationCustomerTicketsResponse
} from '../dip-registration-customer-identification/models/dip-registration-customer-tickets-response.model';
import {DipRegistrationContextService} from '../core/services/dip-registration-context.service';
import {DipRegistrationConfirmService} from '../core/services/dip-registration-confirm.service';
import {DipRegistrationLoggerService} from '../core/services/logger/dip-registration-logger.service';
import {TranslateService} from '@ngx-translate/core';
import {DiprsSpaLoggerService} from '../core/services/logger/diprs-spa-logger.service';
import {DipRegistrationLoggingNames} from '../core/enum/dip-registration-logging-names.enum';
import {DipRegistrationNavigationService} from '../core/services/dip-registration-navigation.service';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {LoggerBackEndCallEventContent, LoggerEventEnum} from '../core/models/dip-registration-log-information.model';
import {IdleService} from '../shared/services/idle.service';
import {ModalService} from '../shared/components/modal/modal.service';

@Component({
    selector: 'dip-registration-phone-number-list',
    templateUrl: './dip-registration-phone-number-list.component.html',
    styleUrls: ['./dip-registration-phone-number-list.component.scss'],
    standalone: false
})
export class DipRegistrationPhoneNumberListComponent implements OnInit, OnDestroy {
  phoneNumberForm: UntypedFormGroup;
  readonly phonePattern = /^[0-9]{10}$/;
  phoneNumberMaskOptions = {
    mask: '(000) 000-0000',
    lazy: true,
    overwrite: true
  };
  isSubmitClicked = false;

  constructor(
    private router: Router,
    private errorHandler: DipRegistrationErrorsHandler,
    private dipseModalService: ModalService,
    private dipRegistrationIdleService: IdleService,
    private fb: UntypedFormBuilder,
    private dipRegistrationConfirmService: DipRegistrationConfirmService,
    private readonly dipRegistrationService: DipRegistrationService,
    private readonly dipRegistrationContextService: DipRegistrationContextService,
    private readonly dipRegistrationLoggerService: DipRegistrationLoggerService,
    private dipRegistrationNavigationService: DipRegistrationNavigationService,
    private translateService: TranslateService,
    private spaLoggerService: DiprsSpaLoggerService
  ) {}

  ngOnInit() {
    this.spaLoggerService.info(LoggerEventEnum.PAGE_ENTERED, DipRegistrationLoggingNames.PHONE_NUMBER_LIST, { previousPageId: this.dipRegistrationNavigationService.getPreviousUrl() });
    this.dipRegistrationService.startKeepingSessionAlive();
    this.dipRegistrationIdleService.start(this.dipRegistrationContextService.getSessionWarningTimeout());
    this.dipRegistrationConfirmService.start();
    this.createForm();
  }

  createForm(): void {
    this.phoneNumberForm = this.fb.group({
      phoneNumber: new UntypedFormControl('', Validators.compose([Validators.required, Validators.pattern(this.phonePattern)]))
    });
  }

  async showModal(button: HTMLButtonElement) {
    this.spaLoggerService.info(LoggerEventEnum.USER_ACTION, DipRegistrationLoggingNames.PHONE_NUMBER_LIST, { id: 'PHONE_NUMBER_POPUP'});
    this.dipseModalService.create<any, void>(
      DipRegistrationPhoneNumberModalComponent,
      (containerInstance, instance) => {
        instance.close.subscribe(containerInstance.destroy);
        containerInstance.modalDialogClass = 'modal-md';
        containerInstance.closeLabel = this.translateService.instant('DIPRSRegistration.modal.close.label');
      },
      button
    ).toPromise();
  }

  async submit() {
    this.spaLoggerService.info(LoggerEventEnum.USER_ACTION, DipRegistrationLoggingNames.PHONE_NUMBER_LIST, { id: 'CONTINUE'});
    if (this.phoneNumberForm.valid) {
      this.isSubmitClicked = true;
      const selectedPhoneId = this.phoneNumberForm.get('phoneNumber').value;
      if (selectedPhoneId) {
        this.dipRegistrationConfirmService.stop();
        await this.callCustomerTicket(selectedPhoneId);
      }
    } else {
      this.validateAllFormFields(this.phoneNumberForm);
    }
  }

  async callCustomerTicket(selectedPhoneNumberId?: string) {
    let start: number;
    let end: number;
    let result: HttpResponse<DipRegistrationCustomerTicketsResponse> | HttpErrorResponse = null;
    let errorInfo: any = {};
    try {
      start = performance.now();
      result = await this.dipRegistrationService.getCustomerTickets(selectedPhoneNumberId);
      end = performance.now();
      if (result.body) {
        this.dipRegistrationConfirmService.stop();
        this.spaLoggerService.info(LoggerEventEnum.FORWARD, DipRegistrationLoggingNames.PHONE_NUMBER_LIST, { forwardUrl: this.dipRegistrationContextService.getUrpUrl()});
        await this.reInitContextHack();
        await this.dipRegistrationService.callURP(result.body);
      } else {
        this.dipRegistrationLoggerService.warn('Call to customer tickets is empty for the selected Phone number id', selectedPhoneNumberId);
      }
    } catch (err) {
      end = performance.now();
      let message = err;
      if (err instanceof HttpErrorResponse) {
          message = err.message;
          result = err;
        }
      errorInfo = this.errorHandler.getErrorCodeAndMessage(err.error);
      this.spaLoggerService.error(LoggerEventEnum.FUNCTIONAL_ERROR, DipRegistrationLoggingNames.PHONE_NUMBER_LIST, { description: message, userFeedbackMessage: 'Error Code: ' + errorInfo.errorCode});
      await this.errorHandler.handleError(err);
    } finally {
      const eventAdditionalInfo: LoggerBackEndCallEventContent = {
        backEndUrl: result.url,
        httpMethod: 'POST',
        httpStatusCode: result.status,
        executionTime: Math.round(end - start),
        errorCode: errorInfo.errorCode,
        backEndEndpoint: result.url.split('/').pop()
      };
      this.spaLoggerService.info(LoggerEventEnum.BACKEND_CALL, DipRegistrationLoggingNames.PHONE_NUMBER_LIST, eventAdditionalInfo);
    }
  }

  private async reInitContextHack() {
    /*
      DISERV-984

      This additional call to initContext() allows for the passing of the environmentId param (if provided) to the BE code
      Context endpoint uses this code to correctly provide the details based on that environmentId ie uapConfig.consumerAppId
      Which is required when redirecting to URP
      This allows URP to correctly point to the same environment we have defined in the environmentId param
      Previously this would default to the value in standalone.xml
    */
    let language = this.dipRegistrationContextService.getLanguage();
    await this.dipRegistrationService.initContext();
    this.dipRegistrationContextService.setLanguage(language);
  }

  validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({onlySelf: true});
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  async selectNoAccess() {
    this.spaLoggerService.info(LoggerEventEnum.USER_ACTION, DipRegistrationLoggingNames.PHONE_NUMBER_LIST, { id: 'NO_PHONE_NUMBER_ACCESS'});
    this.isSubmitClicked = true;
    await this.callCustomerTicket('');
  }

  async cancel(button: HTMLButtonElement) {
    this.spaLoggerService.info(LoggerEventEnum.USER_ACTION, DipRegistrationLoggingNames.PHONE_NUMBER_LIST, { id: 'CANCEL'});
    await this.router.navigate(['overview'], this.dipRegistrationContextService.buildParams({
      needConfirmation: true,
      button
    }));
  }

  ngOnDestroy() {
    this.spaLoggerService.info(LoggerEventEnum.PAGE_EXITED, DipRegistrationLoggingNames.PHONE_NUMBER_LIST, null);
    this.dipRegistrationService.stopKeepingSessionAlive();
  }
}
